import React, { useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GET_PERMITS, GetPermitsData, GetPermitsVars, transformPermitRaw } from "common/graphql/queries/GetPermits";
import { PermitsListPageContent } from "./PermitsListPageContent";
import { PermitsListPageSkeleton } from "./PermitsListPageSkeleton";

export const PermitsListPage: React.FC = () => {
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useQuery<GetPermitsData, GetPermitsVars>(GET_PERMITS, {
    variables: {
      offset: 0,
      limit: 100,
      orderBy: [{ created_at: "desc" }],
    },
    fetchPolicy: "cache-and-network",
  });

  const permits = useMemo(() => (data?.permits || []).map(transformPermitRaw), [data]);

  const handlePermitClick = useCallback(
    (permitId: string) => {
      navigate(`/permits/${permitId}`);
    },
    [navigate],
  );

  const handleApplyClick = useCallback(() => {
    navigate("/permits/apply");
  }, [navigate]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Loading state
  if (loading && !data) {
    return <PermitsListPageSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          Failed to load permits. Please try again.
        </Alert>
      </Box>
    );
  }

  return <PermitsListPageContent permits={permits} onPermitClick={handlePermitClick} onApplyClick={handleApplyClick} />;
};

export default PermitsListPage;
